(function () {
  if (window._trunkshowformLoaded) return;
  window._trunkshowformLoaded = true;

  function _optionalChain(ops) {
    let lastAccessLHS = undefined;
    let value = ops[0];
    let i = 1;
    while (i < ops.length) {
      const op = ops[i];
      const fn = ops[i + 1];
      i += 2;
      if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) {
        return undefined;
      }
      if (op === 'access' || op === 'optionalAccess') {
        lastAccessLHS = value;
        value = fn(value);
      } else if (op === 'call' || op === 'optionalCall') {
        value = fn((...args) => value.call(lastAccessLHS, ...args));
        lastAccessLHS = undefined;
      }
    }

    return value;
  } /* eslint-disable camelcase */

  const keyParameters = [
    'gclid',
    'utm_campaign',
    'utm_source',
    'utm_medium',
    'utm_term',
    'utm_content',
    'fbclid',
  ];

  class TrunkShowForm extends HTMLElement {
    constructor() {
      super();

      this.citySelect = this.querySelector('#city');
      this.countrySelect = this.querySelector('#country');
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('button[type="submit"]');
      this.tokenInput = this.querySelector('input[name="ct"]');
      this.countriesAndCitiesInfo = document.querySelector('#trunkShow_countries_and_cities')
        ? JSON.parse(document.querySelector('#trunkShow_countries_and_cities').innerText)
        : [];
      this.locations = this.getLocationInfo(this.countriesAndCitiesInfo);

      if (!this.countrySelect)
        throw new Error('Trunk show form: Cannot find country select element');
      if (!this.citySelect) throw new Error('Trunk show form: Cannot find city select element');
      if (!this.form) throw new Error('Trunk show form: Cannot find form element');
      if (!this.tokenInput) throw new Error('Trunk show form: Could not find token input element');

      this.countrySelect.addEventListener('change', this.onCountryChanged.bind(this));
      this.form.addEventListener('submit', this.submit.bind(this));
      this.citySelect.addEventListener('change', this.onCityChanged.bind(this));

      const successfulSubmission =
        new URLSearchParams(window.location.search).get('success') === 'true';

      const utmParams = this.getUrlParamsForForm();

      for (const param of keyParameters) {
        if (utmParams[param]) {
          const input = document.querySelector(`input[data-utm="${param}"]`);

          if (input) {
            input.setAttribute('value', utmParams[param]);
          }
        }
      }

      if (successfulSubmission) {
        _optionalChain([
          this,
          'access',
          (_) => _.querySelector,
          'call',
          (_2) => _2('.form-status'),
          'optionalAccess',
          (_3) => _3.classList,
          'access',
          (_4) => _4.remove,
          'call',
          (_5) => _5('hidden'),
        ]);
        _optionalChain([
          this,
          'access',
          (_6) => _6.querySelector,
          'call',
          (_7) => _7('.captcha-wrapper'),
          'optionalAccess',
          (_8) => _8.classList,
          'access',
          (_9) => _9.add,
          'call',
          (_10) => _10('hidden'),
        ]);
      }
    }

    connectedCallback() {
      const OFFSET = 100;

      document.querySelectorAll('[href="#trunk-show-form"]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          const target = document.querySelector('.trunk-show-form');
          if (target) {
            const y = target.getBoundingClientRect().top + window.scrollY - OFFSET;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }
        });
      });
    }

    getLocationInfo(data) {
      const infoMap = new Map();
      data.map((x) => {
        if (infoMap.has(x.heading)) {
          infoMap.set(x.heading, [...infoMap.get(x.heading), x.city]);
        } else {
          infoMap.set(x.heading, [x.city]);
        }
      });

      return infoMap;
    }

    onCountryChanged() {
      const selectedCountry = this.countrySelect.value;
      this.clearCityOptions();

      if (!selectedCountry) {
        _optionalChain([
          this,
          'access',
          (_11) => _11.querySelector,
          'call',
          (_12) => _12('#summary-location'),
          'optionalAccess',
          (_13) => _13.classList,
          'access',
          (_14) => _14.add,
          'call',
          (_15) => _15('hidden'),
        ]);
        this.onCityChanged();

        return;
      }

      _optionalChain([
        this,
        'access',
        (_16) => _16.querySelector,
        'call',
        (_17) => _17('#summary-location'),
        'optionalAccess',
        (_18) => _18.classList,
        'access',
        (_19) => _19.remove,
        'call',
        (_20) => _20('hidden'),
      ]);
      const summaryDisplayCountry = this.querySelector('#summary-location .summary-line__value');
      if (summaryDisplayCountry) {
        summaryDisplayCountry.innerText = selectedCountry;
      }

      const selectedLocationCities = this.locations.get(selectedCountry);

      if (_optionalChain([selectedLocationCities, 'optionalAccess', (_21) => _21.length])) {
        _optionalChain([
          selectedLocationCities,
          'optionalAccess',
          (_22) => _22.forEach,
          'call',
          (_23) =>
            _23((x) => {
              const option = document.createElement('option');
              option.value = x;
              option.label = x;
              this.citySelect.options.add(option);
            }),
        ]);
      }
    }

    onCityChanged() {
      const selectedCity = this.citySelect.value;
      const summaryCity = this.querySelector('#summary-city');
      const summaryDisplayCity = this.querySelector('#summary-city .summary-line__value');

      if (!selectedCity && summaryCity) {
        summaryCity.classList.add('hidden');
      } else if (summaryCity && summaryDisplayCity) {
        summaryCity.classList.remove('hidden');
        summaryDisplayCity.innerText = selectedCity;
      }
    }

    async submit(e) {
      e.preventDefault();
      this.toggleSubmitting();

      const token = this.getCaptchaToken();
      if (!token) {
        this.toggleSubmitting();
        this.toggleCustomErrorMessage('Please complete the captcha to submit your form');

        return;
      }

      const recaptchaValid = await this.validateCaptcha(token);
      if (recaptchaValid === false) {
        this.toggleSubmitting();
        this.toggleCustomErrorMessage('Captcha verification failed. Please try again.');

        return;
      }

      try {
        const formData = new FormData(this.form);
        const formObject = Object.fromEntries(formData.entries());
        const urlParams = this.getUrlParamsForForm();

        let recipient = '';

        let html =
          // eslint-disable-next-line quotes
          "<p>You received a new message from your online store's trunk show appointment form.</p></br></hr>";

        for (const [key, value] of Object.entries(formObject)) {
          let key_ = key;
          if (key === 'g-recaptcha-response') continue;

          if (key === 'Notes') {
            key_ = 'Message';
          }

          html += `<p style="margin:0 5px">${this.capitalize(key_)}: ${value}</p>`;
        }

        const utmParamValues = Object.entries(urlParams);
        if (utmParamValues.length) {
          for (const [key, value] of utmParamValues) {
            html += `<p style="display:none">${this.capitalize(key)}: ${value}</p>`;
          }
        }

        switch (
          _optionalChain([
            formData.get('location'),
            'optionalAccess',
            (_24) => _24.toLowerCase,
            'call',
            (_25) => _25(),
          ])
        ) {
          case 'usa':
            recipient = 'ed.turco@huntsmansavilerow.com';
            break;
          case 'middle east':
            recipient = 'krishan@huntsmansavilerow.com';
            break;
          case 'asia':
            recipient = 'michael.cheng@huntsmansavilerow.com';
            break;
          case 'europe':
            recipient = 'daniel.mullady@huntsmansavilerow.com';
            break;
          default:
            recipient = 'e-shop@huntsmansavilerow.com';
            break;
        }

        const email = {
          to: recipient,
          from: 'e-shop@huntsmansavilerow.com',
          subject: `Trunk show ${formData.get('location')} enquiry`,
          html,
        };

        // const response = await this.sendEmail(email);
        // this.toggleSubmitting();
        // if (response.ok === false) {
        //   _optionalChain([document, 'access', _26 => _26.querySelector, 'call', _27 => _27('.form__message.form__error'), 'optionalAccess', _28 => _28.classList, 'access', _29 => _29.toggle, 'call', _30 => _30('hidden')]);

        //   return;
        // }

        this.form.reset();
        _optionalChain([
          this,
          'access',
          (_31) => _31.querySelector,
          'call',
          (_32) => _32('.form-status'),
          'optionalAccess',
          (_33) => _33.classList,
          'access',
          (_34) => _34.remove,
          'call',
          (_35) => _35('hidden'),
        ]);
        _optionalChain([
          this,
          'access',
          (_36) => _36.querySelector,
          'call',
          (_37) => _37('.captcha-wrapper'),
          'optionalAccess',
          (_38) => _38.classList,
          'access',
          (_39) => _39.add,
          'call',
          (_40) => _40('hidden'),
        ]);
      } catch (error) {
        this.toggleSubmitting();
        _optionalChain([
          document,
          'access',
          (_41) => _41.querySelector,
          'call',
          (_42) => _42('.form__message.form__error'),
          'optionalAccess',
          (_43) => _43.classList,
          'access',
          (_44) => _44.toggle,
          'call',
          (_45) => _45('hidden'),
        ]);
        throw error;
      } finally {
        const salesforceForm = document.getElementById('trunk-show-salesforce-form');
        if (salesforceForm) {
          salesforceForm.submit();
        }
      }
    }

    getCaptchaToken() {
      return this.tokenInput.value;
    }

    async sendEmail(email) {
      return await fetch('/apps/cloth-library/api/email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(email),
      });
    }

    async validateCaptcha(token) {
      const response = await fetch('/apps/cloth-library/api/captcha-verify?token=' + token, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      return data.success;
    }

    toggleSubmitting() {
      if (!this.submitButton) return;

      this.submitButton.classList.toggle('loading');
      _optionalChain([
        this,
        'access',
        (_46) => _46.submitButton,
        'access',
        (_47) => _47.querySelector,
        'call',
        (_48) => _48('.loading-overlay__spinner'),
        'optionalAccess',
        (_49) => _49.classList,
        'access',
        (_50) => _50.toggle,
        'call',
        (_51) => _51('hidden'),
      ]);
      this.submitButton.disabled = !this.submitButton.disabled;
    }

    toggleCustomErrorMessage(message) {
      const errorMessageWrapper = document.querySelector('.trunk-show-form__error-message-wrapper');
      const errorMessage = document.querySelector('.trunk-show-form__error-message');

      if (!errorMessage || !errorMessageWrapper)
        throw new Error('Trunk show form: Could not find error message elements');

      if (!message) {
        errorMessageWrapper.classList.add('hidden');
        errorMessage.innerText = '';
      } else {
        errorMessageWrapper.classList.remove('hidden');
        errorMessage.innerText = message;
        errorMessageWrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    clearCityOptions() {
      _optionalChain([
        this,
        'access',
        (_52) => _52.querySelector,
        'call',
        (_53) => _53('#summary-city'),
        'optionalAccess',
        (_54) => _54.classList,
        'access',
        (_55) => _55.add,
        'call',
        (_56) => _56('hidden'),
      ]);
      this.citySelect.options[0].selected = true;

      while (this.citySelect.options.length > 1) {
        this.citySelect.remove(1);
      }
    }

    capitalize(str) {
      if (str.length === 0) {
        return str;
      }

      return str.charAt(0).toUpperCase() + str.slice(1);
    }

    getUrlParamsForForm() {
      // gclid=123&utm_campaign=spring_sale&utm_source=google&utm_medium=cpc&utm_term=best_sellers&utm_content=ad1&fbclid=456
      const params = new URLSearchParams(window.location.search);
      const keys = [
        'gclid',
        'utm_campaign',
        'utm_source',
        'utm_medium',
        'utm_term',
        'utm_content',
        'fbclid',
      ];
      const result = {};

      keys.forEach((key) => {
        if (params.has(key)) {
          result[key] = params.get(key);
        }
      });

      return result;
    }
  }

  if (!customElements.get('trunk-show-form')) {
    customElements.define('trunk-show-form', TrunkShowForm);
  }
})();
